import { SubmitHandler, UseFormReset } from "react-hook-form";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import {
  ExceptionFormInput,
  SetExceptionDialogPayload,
  SetExceptionDialogProps,
} from "../SetExceptionDialog";
import { ExceptionMutationAction } from "~/operations";
import { useScope } from "~/hooks/useScope";
import { useState } from "react";
import { useSpaceSettings } from "~/pages/space-settings/hooks/useSpaceSettings";
import { DisabledIcon, FalsePositiveIcon, WorkaroundIcon } from "~/icons";
import { ExtendExceptionDialogPayload } from "../ExtendExceptionDialog";

export enum ExceptionFormValidUntil {
  Custom = "CUSTOM",
  Week1 = "WEEK1",
  Month1 = "MONTH1",
  Month3 = "MONTH3",
  Month6 = "MONTH6",
  Indefinitely = "INDEFINITELY",
}

export enum ExceptionFormScope {
  Asset = "asset",
  Space = "space",
}

interface UseSetExceptionDialog {
  onSave:
    | ExtendExceptionDialogPayload["onSave"]
    | SetExceptionDialogPayload["onSave"];
  onClose: SetExceptionDialogProps["onClose"];
  reset: UseFormReset<ExceptionFormInput>;
  onAsset?: SetExceptionDialogPayload["targetData"]["onAsset"];
}

export function useSetExceptionModal({
  onSave,
  onClose,
  reset,
  onAsset,
}: UseSetExceptionDialog) {
  const { spaceScope } = useScope();
  const { exceptionsAllowIndefiniteValidUntil } = spaceScope
    ? useSpaceSettings({
        space: spaceScope,
      })
    : { exceptionsAllowIndefiniteValidUntil: false };
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const exceptionTypes = [
    {
      label: "Risk Accepted",
      value: ExceptionMutationAction.RiskAccepted,
      tagline: "Accept risk for now, with limit to fix later",
      icon: <ThumbUpOffAltIcon />,
    },
    // Not using proper type at the moment
    {
      label: "Workaround",
      value: ExceptionMutationAction.Workaround,
      tagline: "Protections are in place for this finding",
      icon: <WorkaroundIcon />,
    },
    {
      label: "False Positive",
      value: ExceptionMutationAction.FalsePositive,
      tagline: "This is not a real threat",
      icon: <FalsePositiveIcon />,
    },
    {
      label: "Disable",
      value: ExceptionMutationAction.Disable,
      tagline: "Stop scanning, this check breaks things",
      icon: <DisabledIcon />,
    },
  ];

  const scopeOptions = [
    {
      label: `Single Asset: ${onAsset?.name}`,
      value: onAsset?.mrn,
      key: ExceptionFormScope.Asset,
    },
    {
      label: "Entire space",
      value: spaceScope?.mrn,
      key: ExceptionFormScope.Space,
    },
  ];

  const timeLimitOptions = [
    {
      label: "Set number of days",
      value: ExceptionFormValidUntil.Custom,
    },
    {
      label: "1 Week",
      value: ExceptionFormValidUntil.Week1,
    },
    {
      label: "1 Month",
      value: ExceptionFormValidUntil.Month1,
    },
    {
      label: "3 Months",
      value: ExceptionFormValidUntil.Month3,
    },
    {
      label: "6 Months",
      value: ExceptionFormValidUntil.Month6,
    },
    // Only show indefinite option if the space setting is enabled
    ...(exceptionsAllowIndefiniteValidUntil
      ? [
          {
            label: "Indefinitely",
            value: ExceptionFormValidUntil.Indefinitely,
          },
        ]
      : []),
  ];

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFormSubmit: SubmitHandler<ExceptionFormInput> = async (
    formData,
  ) => {
    setIsLoading(true);
    try {
      await onSave(formData as never);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      handleClose();
    }
  };

  return {
    isLoading,
    scopeOptions,
    timeLimitOptions,
    exceptionTypes,
    handleClose,
    form: { handleFormSubmit },
  };
}
